import { Button, Tooltip } from '@dixa/lib-shared-habla';
import { isQueryError, isResponseWithData } from 'lib-shared-auth';
import { useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

type RetryMessageButtonProps = {
  /** The id of the conversation the message belongs to. */
  csid: number;
  /** The id of the failed message to send again. */
  messageId: string;
};

const errorTexts: Partial<Record<number, string>> = {
  400: 'This message cannot be sent again.',
  404: 'The message could not be found.',
  409: 'The message is already being sent again.',
};

/** Sends a failed message again. */
export const RetryMessageButton = ({
  csid,
  messageId,
}: RetryMessageButtonProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const isAssignedToMe = useAppSelector(
    ({ conversation: { metaConversation } }) =>
      metaConversation.conversation.id === csid &&
      metaConversation.isAssignedToMe,
  );
  const [retryMessage, { isLoading }] =
    conversationsApi.useRetryMessageMutation();

  const handleClick = useCallback(async () => {
    dispatch(
      conversationActions.conversationRetryMessageStarted({ csid, messageId }),
    );
    const response = await retryMessage({ csid, messageId });

    if (isResponseWithData(response)) {
      dispatch(
        conversationActions.conversationRetryMessageSucceeded({
          csid,
          messageId,
          timestamp: new Date().toISOString(),
        }),
      );
      return;
    }

    dispatch(
      conversationActions.conversationRetryMessageFailed({ csid, messageId }),
    );

    if (isQueryError(response.error) && response.error.status === 403) {
      return;
    }

    notify(
      {
        title: 'Message not sent',
        text:
          (isQueryError(response.error) &&
            errorTexts[Number(response.error.status)]) ||
          'The message could not be sent. Please try again.',
        time: 5000,
      },
      NotificationType.Error,
    );
  }, [csid, dispatch, messageId, notify, retryMessage]);

  const button = (
    <Button
      variant="tertiary"
      icon="redo"
      isLoading={isLoading}
      disabled={!isAssignedToMe}
      onClick={handleClick}
    >
      Retry
    </Button>
  );

  if (isAssignedToMe) {
    return button;
  }

  return (
    <Tooltip wrap="span" content="You need to be assigned to retry">
      {button}
    </Tooltip>
  );
};
